import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import { ArrowRight, Mail } from 'lucide-react';

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 tracking-tight">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-neutral-700">{children}</div>
    </section>
  );
}

export default function TermsPage() {
  const { lang } = useT();
  return (
    <div className="pt-28 pb-20">
      <SEO
        title={lang === 'es' ? 'Términos y Condiciones' : 'Terms & Conditions'}
        description="Kitxens Terms & Conditions — service usage, user responsibilities, intellectual property and applicable law."
        path="/terms"
      />
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-3">LEGAL</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 tracking-[-0.02em]">
            Terms &amp; Conditions
          </h1>
          <p className="mt-3 text-sm text-neutral-500">Last updated: 2026</p>
        </motion.div>

        <Section title="Object">
          <p>
            This document constitutes an adherence contract for the use of any of the Internet pages at
            <a href="https://www.kitxens.com" className="text-[#F77F11] underline ml-1">www.kitxens.com</a>
            or other linked domains (the “Website”): on one part, PUE MEXICO S.A.P.I. DE C.V. (“Kitxens” and/or the “Company”), as responsible, and on the other part, the User, both parties subjecting themselves to what is established in this document.
          </p>
        </Section>

        <Section title="Acceptance of Terms">
          <p>
            By entering and using this Internet portal, identified with the domain name www.kitxens.com (property of PUE MEXICO SAPI DE CV), the User accepts these Terms and Conditions and expressly declares acceptance using electronic means, in terms of Article 1803 and related provisions of the Federal Civil Code (México).
          </p>
          <p>
            For this contract, the parties agree that “User” means any person of any nature who enters www.kitxens.com and/or any subpage, and/or any person who registers or uses any of the services offered.
          </p>
          <p>
            If the User does not fully accept these Terms, the User must refrain from accessing, using and observing the Website. Continued use of the Site constitutes absolute and express acceptance of these Terms, of any documents incorporated by reference, and of the applicable laws.
          </p>
          <p>
            Kitxens does not keep an individualized copy of this agreement; the User is encouraged to retain a copy for their own records. Violations of these Terms may result in cancellation of access and any legal action Kitxens deems appropriate.
          </p>
        </Section>

        <Section title="Use of the Website">
          <p>To use the Website, the User must be at least 18 years old, or accessing under the supervision of a parent or legal guardian.</p>
          <p>
            Kitxens grants a non-transferable, revocable license to use the Website under these Terms. The User may only print or copy information from the Website for personal use; commercial use of such information is strictly prohibited.
          </p>
          <p>
            Legal entities are subject to article 148, section IV of the Federal Law of Copyright. Reprinting, publishing, distributing, sublicensing, selling or reproducing any information, image, document or graphic from the Website for any use other than non-commercial personal use is prohibited without prior written authorization from Kitxens.
          </p>
          <p>
            Certain services and features may require registration. By providing personal information, the User grants Kitxens the authorization referred to in article 109 of the Federal Law of Copyright, and commits to providing accurate and up-to-date information.
          </p>
          <p>
            Each User is solely responsible for keeping passwords and account credentials secure, and for all activity under their account. The User must notify Kitxens of any unauthorized use. Kitxens will not be responsible, directly or indirectly, for any loss arising from the User’s non-compliance with this section.
          </p>
          <p>
            Kitxens may block or remove any content that is abusive, defamatory, obscene, fraudulent, deceptive, infringing of intellectual property rights, or that otherwise contravenes this contract.
          </p>
          <p>
            Users are prohibited from violating or attempting to violate the security of the Website or its affiliates, including accessing data they are not authorized to use, probing or testing vulnerabilities without authorization, interfering with other Users, transmitting viruses, flooding, spamming or forging packet headers. Such conduct may result in civil or criminal liability.
          </p>
          <p>
            Users must have the necessary equipment for Internet access (computer, telephone, smartphone, modem, programs, etc.); the use of such equipment is entirely their responsibility. Kitxens is exempt from any responsibility for interruptions of Internet service, failures of telecommunications systems, power supply issues, force majeure or third-party actions.
          </p>
        </Section>

        <Section title="Intellectual & Industrial Property">
          <p>
            Kitxens is the sole owner of all intellectual property rights (registered or unregistered) on www.kitxens.com and its subdomains, including projects, software, source code, graphics, photographs, videos, images, music, sound, texts, logos, trademarks, domain names, trade names and data. The Website content is also protected as a collective work under Mexican copyright laws and international conventions. All rights reserved.
          </p>
          <p>
            Copying, reproducing, adapting, modifying, distributing, marketing, licensing, sending, disclosing or publicly communicating the contents of the Site without prior written authorization from Kitxens is prohibited.
          </p>
          <p>
            If the User transmits any information, programs, applications, software or material that requires licensing through www.kitxens.com, the User grants Kitxens a perpetual, universal, free, non-exclusive, worldwide, royalty-free license, including the right to sublicense, sell, reproduce, distribute, transmit, create derivative works, display and perform such material publicly.
          </p>
          <p>
            The same applies to any feedback, questions, criticisms, comments or suggestions the User sends. By submitting reviews, the User also grants Kitxens the right to use the name they provide in the context of such review or content.
          </p>
          <p>
            If the User believes content on the Website violates intellectual property rights, they may notify Kitxens through customer service, providing: (i) accurate personal data; (ii) handwritten signature; (iii) precise indication of the protected content and its location on the Website; (iv) a clear declaration that the introduction of such content was not consented; and (v) a sworn statement that the information provided is accurate.
          </p>
          <p>
            The Website contains links to third-party sites for convenience. Kitxens does not endorse or take responsibility for the content of those sites; access by the User is at their own risk.
          </p>
        </Section>

        <Section title="Advertising Material">
          <p>
            Parts of the Website may contain information, images, advertisements or other promotional material from third-party sponsors. Sponsors are responsible for ensuring that their material complies with applicable laws and regulatory codes. Kitxens is not responsible for errors or inaccuracies in advertising materials. Such material is protected by intellectual and industrial property laws.
          </p>
        </Section>

        <Section title="Liability & Indemnification">
          <p>
            The User agrees to indemnify and hold harmless Kitxens, its subsidiaries, controlling company, shareholders, directors, employees, officials, advisors and agents against any actions, proceedings, liabilities, claims, losses, damages, costs and expenses (including attorney fees) arising from (i) the violation of these Terms or (ii) any applicable law or regulation.
          </p>
          <p>
            Kitxens reserves the right to assume the defense and control of any matter or claim that could imply compensation associated with any breach by the User. The User agrees to cooperate in the development of the relevant defenses.
          </p>
        </Section>

        <Section title="Modifications, Validity & Termination">
          <p>
            Kitxens may at any time, and without notice, make corrections, additions or modifications to the content, presentation, information, services and other elements of the Website. The User has no right to claim compensation as a result.
          </p>
          <p>
            These Terms are of unlimited duration and become effective upon their publication on the Site. Kitxens may alter this document without prior notice; modifications become effective immediately upon publication. By continuing to use the Site, the User is presumed to have read and consented to the updated Terms. If the User does not accept the modified Terms, they must stop using the Website. Kitxens may suspend access to the Website or terminate these Terms at any time, without obligation to indemnify the User.
          </p>
        </Section>

        <Section title="Survival, Assignment & Waiver">
          <p>
            These Terms constitute the entire agreement between the parties. Any clause legally declared invalid will be eliminated or modified at the choice of Kitxens; the rest of the Terms remain in force.
          </p>
          <p>
            The rights granted to the User are personal and may not be assigned or transmitted. Kitxens may assign its rights and obligations to any third party, subsidiary or controlling company without prior authorization.
          </p>
          <p>
            Inactivity by Kitxens, its affiliates or suppliers in the exercise of any right under this contract shall not be interpreted as a waiver of such rights.
          </p>
        </Section>

        <Section title="Applicable Law & Jurisdiction">
          <p>
            These Terms are interpreted and governed by the legislation in force in Mexico, Mexico City, renouncing the application of the Convention on the International Sale of Goods.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            PUE MEXICO S.A.P.I. DE C.V.<br />
            <a href="https://www.kitxens.com" className="text-[#F77F11] underline">www.kitxens.com</a><br />
            <a href="mailto:info@kitxens.com" className="text-[#F77F11] underline">info@kitxens.com</a>
          </p>
        </Section>

        <div className="mt-12 p-6 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-neutral-900">Questions about these terms?</p>
            <p className="text-sm text-neutral-600">Reach out to our team — we’re happy to clarify.</p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white px-5 h-11 rounded-full text-sm font-semibold whitespace-nowrap"
          >
            <Mail className="w-4 h-4" /> Contact us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
